import { computed, makeObservable } from 'mobx';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { EventsGroupState, UserViewSport, UserViewCompetition, UserViewDate } from './EventsGroupState';
import { getMarketAndSelections, PossibleMarketAndSelectionInListType } from './getMarketAndSelections';
import { DateTime } from 'src_common/utils/time/time';
import { getSimpleDateUnified } from 'src/utils/date';

// TODO: Translation
export const SELECTIONS_LABELS = (
    getTranslation: (key: string, defaultText: string) => string
): Record<string, string> => ({
    S: getTranslation('market.score', 'Score'),
    H: getTranslation('market.home', 'Home'),
    T: getTranslation('market.tie', 'Tie'),
    D: getTranslation('market.draw', 'Draw'),
    A: getTranslation('market.away', 'Away'),
    O: getTranslation('market.over', 'Over'),
    U: getTranslation('market.under', 'Under'),
    Y: getTranslation('market.yes', 'Y'),
    N: getTranslation('market.no', 'N'),
    'unnamed-favourite': getTranslation('market.uf', 'UF'),
    'tennis:H': getTranslation('market.player-1', 'Player 1'),
    'tennis:A': getTranslation('market.player-2', 'Player 2'),
    'boxing:H': '1',
    'boxing:D': 'X',
    'boxing:A': '2',
    'mixedmartialarts:H': '1',
    'mixedmartialarts:A': '2',
});

const getEventDateKey = (
    event: EventListGroupEventItemType,
    getTranslation: (key: string, defaultText: string) => string
): string => {
    const eventModel = event.id2.getEventModel();

    return eventModel === null
        ? getSimpleDateUnified(DateTime.from(event.startTime)?.toISOString() ?? 'time error', getTranslation)
        : getSimpleDateUnified(eventModel.timeSettingsStartTime, getTranslation);
};

const groupEventsByDate = (
    events: ReadonlyArray<EventListGroupEventItemType>,
    getTranslation: (key: string, defaultText: string) => string
): Map<string, Array<EventListGroupEventItemType>> => {
    return events.reduce((map, event) => {
        const date = getEventDateKey(event, getTranslation);

        let eventsForDate = map.get(date);

        if (eventsForDate === undefined) {
            eventsForDate = [];
            map.set(date, eventsForDate);
        }

        eventsForDate.push(event);

        return map;
    }, new Map<string, Array<EventListGroupEventItemType>>());
};

export class EventsGroupViewDate {
    public readonly date: string;
    private readonly dateController: UserViewDate;
    public readonly events: ReadonlyArray<EventListGroupEventItemType>;

    public constructor(date: string, dateController: UserViewDate, events: ReadonlyArray<EventListGroupEventItemType>) {
        this.date = date;
        this.dateController = dateController;
        this.events = events;
    }

    public get visible(): boolean {
        return this.dateController.visible;
    }

    public toggle = (): void => {
        this.dateController.toggle();
    };
}

export class EventsGroupViewCompetition {
    public readonly state: EventsGroupState;
    public readonly sport: string;
    public readonly name: string;
    public readonly id: number;
    private readonly competitionController: UserViewCompetition;
    public readonly events: Array<EventListGroupEventItemType>;

    public constructor(
        state: EventsGroupState,
        sport: string,
        name: string, //competition name
        id: number,
        competitionController: UserViewCompetition,
        events: Array<EventListGroupEventItemType>
    ) {
        makeObservable(this);
        this.state = state;
        this.sport = sport;
        this.name = name;
        this.id = id;
        this.competitionController = competitionController;
        this.events = events;
    }

    public get visible(): boolean {
        return this.competitionController.visible;
    }

    @computed public get eventsByDate(): Array<EventsGroupViewDate> {
        return Array.from(groupEventsByDate(this.events, this.state.getTranslation), ([date, events]) => {
            return new EventsGroupViewDate(date, this.state.getDateView(date), events);
        });
    }

    @computed.struct public get possibleMarketAndSelectionInList(): PossibleMarketAndSelectionInListType {
        return getMarketAndSelections(this.events);
    }

    @computed.struct public get possibleMarketAndSelectionInListHeaders(): Array<string> {
        return getMarketAndSelections(this.events).headers;
    }

    public get areEventsTwoParticipantsOnly(): boolean {
        return this.events.some((event) => event.template.startsWith('two-participant'));
    }

    public isFirstEventOutright = (events: Array<EventListGroupEventItemType>): boolean => {
        const first = events[0];
        if (first !== undefined) {
            return first.template === 'outrights';
        }

        return false;
    };

    public get isOutrightEvent(): boolean {
        return this.isFirstEventOutright(this.events);
    }

    @computed.struct public get lastSelectionsHeader(): Array<string> {
        return this.translateSelectionHeaders(this.possibleMarketAndSelectionInList.headers);
    }

    public translateSelectionHeaders = (headers: Array<string>): Array<string> => {
        const selectionsLabels = SELECTIONS_LABELS(this.state.getTranslation);
        return headers.map((x) => selectionsLabels[`${this.sport}:${x}`] ?? selectionsLabels[x] ?? x);
    };
    public toggle = (): void => {
        this.competitionController.toggle();
    };

    public toggleDate = (): void => {
        this.competitionController.toggle();
    };
}

export class EventsGroupViewSport {
    public readonly state: EventsGroupState;
    public readonly id: string;
    private readonly sportController: UserViewSport;
    public readonly competitions: ReadonlyArray<EventsGroupViewCompetition>;

    public constructor(
        state: EventsGroupState,
        id: string,
        sportController: UserViewSport,
        competitions: ReadonlyArray<EventsGroupViewCompetition>
    ) {
        makeObservable(this);
        this.state = state;
        this.id = id;
        this.sportController = sportController;
        this.competitions = competitions;
    }

    public get visible(): boolean {
        return this.sportController.visible;
    }

    public toggle = (): void => {
        this.sportController.toggle();
    };

    @computed public get allEvents(): Array<EventListGroupEventItemType> {
        return this.competitions
            .flatMap((competition) => competition.events)
            .slice()
            .sort((a, b) => a.startTime - b.startTime);
    }

    @computed public get eventsByDate(): Array<EventsGroupViewDate> {
        return Array.from(groupEventsByDate(this.allEvents, this.state.getTranslation), ([date, events]) => {
            return new EventsGroupViewDate(date, this.state.getDateView(date), events);
        });
    }

    @computed.struct public get possibleMarketAndSelectionInListHeaders(): Array<string> {
        return getMarketAndSelections(this.allEvents).headers;
    }

    @computed public get eventCompetitionByEventId(): Map<number, EventsGroupViewCompetition> {
        const map = new Map<number, EventsGroupViewCompetition>();

        for (const competition of this.competitions) {
            for (const event of competition.events) {
                map.set(event.id, competition);
            }
        }

        return map;
    }

    public translateSelectionHeaders = (headers: Array<string>): Array<string> => {
        const selectionsLabels = SELECTIONS_LABELS(this.state.getTranslation);
        return headers.map((x) => selectionsLabels[`${this.id}:${x}`] ?? selectionsLabels[x] ?? x);
    };
}

export class EventsGroupView {
    public readonly sports: ReadonlyArray<EventsGroupViewSport>;

    public constructor(sports: ReadonlyArray<EventsGroupViewSport>) {
        this.sports = sports;
    }
}
